import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Select, MenuItem,
  FormControl, InputLabel, Chip, Paper, List, ListItemButton, ListItemText,
  CircularProgress, Alert, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Switch, FormControlLabel, Tooltip, ImageList, ImageListItem,
  ImageListItemBar, Breadcrumbs, Link as MuiLink, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDecisionTree, updateDecisionTree, createDecisionNode, updateDecisionNode,
  deleteDecisionNode, createDecisionOption, updateDecisionOption, deleteDecisionOption,
  uploadDecisionNodeMedia, deleteDecisionNodeMedia,
} from '../services/api';
import { useApiCall } from '../hooks/useApiCall';
import { useNotification } from '../hooks/useNotification';
import DecisionMedia from '../components/DecisionMedia';

const NODE_TYPES = [
  { value: 'question', label: 'Pregunta (con opciones)' },
  { value: 'info', label: 'Información (paso intermedio)' },
  { value: 'leaf', label: 'Resultado final (diagnóstico/conducta)' },
];

const emptyNodeForm = { node_type: 'question', title: '', body_text: '' };

// Debe coincidir con MAX_MEDIA_SIZE en backend/routers/decision_trees.py —
// se valida acá también para avisar antes de subir en vez de esperar el 413.
const MAX_MEDIA_MB = 20;
const MAX_MEDIA_BYTES = MAX_MEDIA_MB * 1024 * 1024;

// Camino desde el nodo raíz hasta `targetId`, siguiendo el primer edge
// entrante que encuentre por cada nodo (BFS hacia atrás). El modelo es un
// grafo, no un árbol estricto — dos caminos distintos pueden reconverger en
// el mismo nodo, así que esto muestra UN camino posible, no "el" camino
// único. Suficiente para orientarse ("cómo se llega hasta acá"), no un
// mapa completo de todas las formas de llegar.
function findPathToNode(nodes, options, rootId, targetId) {
  if (!rootId || !targetId) return [];
  if (rootId === targetId) return [{ nodeId: rootId }];

  const incomingByNode = new Map();
  options.forEach((o) => {
    if (!incomingByNode.has(o.to_node_id)) incomingByNode.set(o.to_node_id, o);
  });

  const path = [];
  let cursor = targetId;
  const visited = new Set();
  while (cursor && !visited.has(cursor)) {
    visited.add(cursor);
    const incoming = incomingByNode.get(cursor);
    path.unshift({ nodeId: cursor, viaLabel: incoming?.label });
    if (cursor === rootId) break;
    cursor = incoming?.from_node_id;
  }
  if (path[0]?.nodeId !== rootId) return []; // no se pudo llegar hasta la raíz (nodo huérfano)
  return path;
}

const AlgoritmoEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const apiCall = useApiCall();
  const notify = useNotification();

  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeForm, setNodeForm] = useState(emptyNodeForm);
  const [savingField, setSavingField] = useState(null); // 'title' | 'body_text' | 'node_type' | null
  const [newNodeOpen, setNewNodeOpen] = useState(false);
  const [newNodeForm, setNewNodeForm] = useState(emptyNodeForm);
  const [titleDraft, setTitleDraft] = useState('');

  // ── Agregar rama (flujo fusionado: crea nodo + opción juntos) ──────────────
  const [branchLabel, setBranchLabel] = useState('');
  const [branchRationale, setBranchRationale] = useState('');
  const [branchImageId, setBranchImageId] = useState('');
  const [linkExistingOpen, setLinkExistingOpen] = useState(false);
  const [linkExistingTarget, setLinkExistingTarget] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [uploadingBranchImage, setUploadingBranchImage] = useState(false);

  const [editingOption, setEditingOption] = useState(null);
  const [editOptionForm, setEditOptionForm] = useState({ label: '', to_node_id: '', rationale_text: '', image_media_id: '' });
  const [savingOption, setSavingOption] = useState(false);
  const [uploadingEditOptionImage, setUploadingEditOptionImage] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getDecisionTree(id);
      setTree(r.data);
      setTitleDraft(r.data.title);
      // Forma funcional: lee siempre el selectedNodeId más reciente, no el
      // capturado cuando este callback se creó (evita selección stale tras
      // recargas repetidas del árbol).
      setSelectedNodeId((prev) => {
        if (prev && r.data.nodes.some((n) => n.id === prev)) return prev;
        return r.data.nodes.length > 0 ? (r.data.root_node_id || r.data.nodes[0].id) : null;
      });
    } catch (err) {
      notify(err.userMessage || 'Error al cargar el algoritmo', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const selectedNode = tree?.nodes.find((n) => n.id === selectedNodeId) || null;

  // Orden de creación fijo por id (no cambia si se reconecta el árbol) — da
  // un número simple para referirse a "Nodo #3" en conversación, sin
  // prometer una jerarquía que el grafo no garantiza.
  const nodeNumberById = useMemo(() => {
    if (!tree) return new Map();
    const sorted = [...tree.nodes].sort((a, b) => a.id - b.id);
    return new Map(sorted.map((n, i) => [n.id, i + 1]));
  }, [tree]);

  const allOptions = useMemo(() => tree?.nodes.flatMap((n) => n.options.map((o) => ({ ...o, from_node_id: n.id }))) || [], [tree]);

  const breadcrumbPath = useMemo(() => {
    if (!tree || !selectedNodeId) return [];
    return findPathToNode(tree.nodes, allOptions, tree.root_node_id, selectedNodeId);
  }, [tree, allOptions, selectedNodeId]);

  // Solo resincroniza el form al cambiar de nodo seleccionado, no en cada
  // recarga del árbol (subir media, agregar opciones) — de lo contrario
  // se pisan cambios de título/contenido que el usuario todavía no guardó.
  useEffect(() => {
    if (selectedNode) {
      setNodeForm({
        node_type: selectedNode.node_type,
        title: selectedNode.title,
        body_text: selectedNode.body_text || '',
      });
    }
    setBranchLabel('');
    setBranchRationale('');
    setBranchImageId('');
    setLinkExistingOpen(false);
    setLinkExistingTarget('');
  }, [selectedNodeId]);

  // ── Metadata del árbol ─────────────────────────────────────────────────

  const handleSaveTitle = async () => {
    if (!titleDraft.trim() || titleDraft === tree.title) return;
    try {
      await apiCall(updateDecisionTree(id, { title: titleDraft }), 'Error al guardar el título');
      await load();
    } catch {}
  };

  const handleTogglePublish = async () => {
    try {
      await apiCall(updateDecisionTree(id, { is_published: !tree.is_published }), 'Error al publicar');
      await load();
      notify(tree.is_published ? 'Algoritmo despublicado' : 'Algoritmo publicado', 'success');
    } catch {}
  };

  const handleSetRoot = async (nodeId) => {
    try {
      await apiCall(updateDecisionTree(id, { root_node_id: nodeId }), 'Error al definir el nodo inicial');
      await load();
    } catch {}
  };

  // ── Primer nodo (sin origen — no hay desde dónde tirar una rama) ────────

  const openNewNode = () => {
    setNewNodeForm(emptyNodeForm);
    setNewNodeOpen(true);
  };

  const handleCreateNode = async () => {
    if (!newNodeForm.title.trim()) { notify('El título del nodo es obligatorio', 'error'); return; }
    try {
      const r = await apiCall(createDecisionNode(id, newNodeForm), 'Error al crear el nodo');
      setNewNodeOpen(false);
      await load();
      setSelectedNodeId(r.data.id);
    } catch {}
  };

  // ── Autoguardado de Título/Contenido/Tipo del nodo seleccionado ────────
  // Mismo patrón que el título del árbol arriba de todo (onBlur) — sin
  // botón "Guardar nodo": las opciones de abajo ya se autoguardan al
  // crearlas/editarlas, y tener un botón de guardado manual solo para
  // título/contenido generaba la duda de "¿esto ya quedó guardado o no?".

  const handleNodeFieldBlur = async (field) => {
    if (!selectedNode) return;
    const value = nodeForm[field];
    const original = field === 'body_text' ? (selectedNode.body_text || '') : selectedNode[field];
    if (value === original) return;
    setSavingField(field);
    try {
      await apiCall(updateDecisionNode(id, selectedNodeId, { [field]: value }), 'Error al guardar el nodo');
      await load();
    } catch {} finally {
      setSavingField(null);
    }
  };

  const handleNodeTypeChange = async (newType) => {
    setNodeForm((p) => ({ ...p, node_type: newType }));
    setSavingField('node_type');
    try {
      await apiCall(updateDecisionNode(id, selectedNodeId, { node_type: newType }), 'Error al guardar el nodo');
      await load();
    } catch {} finally {
      setSavingField(null);
    }
  };

  const handleDeleteNode = async () => {
    if (!window.confirm(`¿Eliminar el nodo "${selectedNode.title}"?`)) return;
    try {
      await apiCall(deleteDecisionNode(id, selectedNodeId), 'Error al eliminar el nodo');
      setSelectedNodeId(null);
      await load();
    } catch {}
  };

  // ── Agregar rama: crea el nodo destino Y la opción que apunta a él en un
  // solo paso, reusando el mismo texto para ambos — el caso común (~95%) es
  // que el título del nodo destino y el texto de la opción que lo alcanza
  // sean el mismo concepto ("Sí" lleva a un nodo que se llama "Sí"). Si
  // hace falta que difieran, se edita el título del nodo aparte después.

  const handleAddBranch = async () => {
    if (!branchLabel.trim()) { notify('Escribí el texto de la opción', 'error'); return; }
    setCreatingBranch(true);
    try {
      const nodeRes = await apiCall(
        createDecisionNode(id, { node_type: 'question', title: branchLabel.trim(), body_text: '' }),
        'Error al crear el nodo destino',
      );
      await apiCall(createDecisionOption(id, selectedNodeId, {
        label: branchLabel.trim(),
        to_node_id: nodeRes.data.id,
        rationale_text: branchRationale || null,
        image_media_id: branchImageId || null,
      }), 'Error al conectar la opción');
      setBranchLabel(''); setBranchRationale(''); setBranchImageId('');
      await load();
      notify('Rama creada', 'success');
    } catch {} finally {
      setCreatingBranch(false);
    }
  };

  const handleLinkExisting = async () => {
    if (!branchLabel.trim() || !linkExistingTarget) {
      notify('Completá el texto de la opción y elegí el nodo destino', 'error');
      return;
    }
    setCreatingBranch(true);
    try {
      await apiCall(createDecisionOption(id, selectedNodeId, {
        label: branchLabel.trim(),
        to_node_id: Number(linkExistingTarget),
        rationale_text: branchRationale || null,
        image_media_id: branchImageId || null,
      }), 'Error al conectar la opción');
      setBranchLabel(''); setBranchRationale(''); setBranchImageId('');
      setLinkExistingOpen(false); setLinkExistingTarget('');
      await load();
      notify('Opción conectada', 'success');
    } catch {} finally {
      setCreatingBranch(false);
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('¿Eliminar esta opción? El nodo destino no se borra, solo la conexión.')) return;
    try {
      await apiCall(deleteDecisionOption(id, optionId), 'Error al eliminar la opción');
      await load();
    } catch {}
  };

  const openEditOption = (o) => {
    setEditingOption(o);
    setEditOptionForm({
      label: o.label,
      to_node_id: o.to_node_id,
      rationale_text: o.rationale_text || '',
      image_media_id: o.image_media_id || '',
    });
  };

  const handleSaveOption = async () => {
    if (!editOptionForm.label.trim() || !editOptionForm.to_node_id) {
      notify('Completá el texto de la opción y el nodo destino', 'error');
      return;
    }
    setSavingOption(true);
    try {
      await apiCall(updateDecisionOption(id, editingOption.id, {
        label: editOptionForm.label,
        to_node_id: Number(editOptionForm.to_node_id),
        rationale_text: editOptionForm.rationale_text || null,
        image_media_id: editOptionForm.image_media_id || null,
        clear_image: !editOptionForm.image_media_id,
      }), 'Error al guardar la opción');
      setEditingOption(null);
      await load();
      notify('Opción actualizada', 'success');
    } catch {} finally {
      setSavingOption(false);
    }
  };

  // ── Media ────────────────────────────────────────────────────────────────

  const handleUploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MEDIA_BYTES) {
      notify(`El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB — el máximo permitido es ${MAX_MEDIA_MB} MB`, 'error');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      await apiCall(uploadDecisionNodeMedia(id, selectedNodeId, file), 'Error al subir el archivo');
      await load();
    } catch {} finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await apiCall(deleteDecisionNodeMedia(mediaId), 'Error al eliminar el archivo');
      await load();
    } catch {}
  };

  // Subir una imagen directamente desde "Agregar rama" o el dialog de
  // editar opción, sin ir primero a "Imágenes y videos de referencia" del
  // nodo — queda asociada al nodo origen igual que cualquier otra media.
  const handleUploadBranchImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MEDIA_BYTES) {
      notify(`El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB — el máximo permitido es ${MAX_MEDIA_MB} MB`, 'error');
      e.target.value = '';
      return;
    }
    setUploadingBranchImage(true);
    try {
      const res = await apiCall(uploadDecisionNodeMedia(id, selectedNodeId, file), 'Error al subir el archivo');
      await load();
      setBranchImageId(res.data.id);
    } catch {} finally {
      setUploadingBranchImage(false);
      e.target.value = '';
    }
  };

  const handleUploadEditOptionImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MEDIA_BYTES) {
      notify(`El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB — el máximo permitido es ${MAX_MEDIA_MB} MB`, 'error');
      e.target.value = '';
      return;
    }
    const fromNodeId = editingOption?.from_node_id;
    setUploadingEditOptionImage(true);
    try {
      const res = await apiCall(uploadDecisionNodeMedia(id, fromNodeId, file), 'Error al subir el archivo');
      await load();
      setEditOptionForm((p) => ({ ...p, image_media_id: res.data.id }));
    } catch {} finally {
      setUploadingEditOptionImage(false);
      e.target.value = '';
    }
  };

  if (loading || !tree) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const otherNodes = tree.nodes.filter((n) => n.id !== selectedNodeId);
  const savingIndicator = (field) => savingField === field
    ? <CircularProgress size={14} sx={{ ml: 1 }} />
    : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/algoritmos')}><ArrowBackIcon /></IconButton>
        <TextField
          variant="standard"
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={handleSaveTitle}
          sx={{ flexGrow: 1, '& input': { fontSize: '1.5rem', fontWeight: 500 } }}
        />
        <FormControlLabel
          control={<Switch checked={tree.is_published} onChange={handleTogglePublish} />}
          label={tree.is_published ? 'Publicado' : 'Borrador'}
        />
      </Box>

      {!tree.root_node_id && tree.nodes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Este algoritmo no tiene nodo inicial definido. Marcá uno como inicial con la estrella para poder publicarlo.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* ── Lista de nodos ── */}
        <Paper sx={{ width: 280, flexShrink: 0 }}>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2">Nodos ({tree.nodes.length})</Typography>
            <Tooltip title="Nuevo nodo suelto (sin conectar)">
              <IconButton size="small" onClick={openNewNode}><AddIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
          <Divider />
          <List dense sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {tree.nodes.map((n) => (
              <ListItemButton
                key={n.id}
                selected={n.id === selectedNodeId}
                onClick={() => setSelectedNodeId(n.id)}
              >
                <Tooltip title={n.id === tree.root_node_id ? 'Nodo inicial' : 'Marcar como nodo inicial'}>
                  <IconButton
                    size="small"
                    edge="start"
                    onClick={(e) => { e.stopPropagation(); handleSetRoot(n.id); }}
                    sx={{ mr: 0.5 }}
                  >
                    {n.id === tree.root_node_id
                      ? <StarIcon fontSize="small" color="warning" />
                      : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <ListItemText
                  primary={`#${nodeNumberById.get(n.id)} — ${n.title}`}
                  secondary={NODE_TYPES.find((t) => t.value === n.node_type)?.label}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
            {tree.nodes.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                Creá el primer nodo para empezar
              </Typography>
            )}
          </List>
        </Paper>

        {/* ── Panel de edición del nodo ── */}
        {selectedNode ? (
          <Paper sx={{ flexGrow: 1, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6">
                Nodo #{nodeNumberById.get(selectedNode.id)}
                {selectedNode.id === tree.root_node_id && (
                  <Chip label="Inicial" size="small" color="warning" sx={{ ml: 1, height: 20 }} />
                )}
              </Typography>
              <IconButton color="error" onClick={handleDeleteNode}><DeleteIcon /></IconButton>
            </Box>

            {breadcrumbPath.length > 1 && (
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2, '& .MuiBreadcrumbs-li': { fontSize: '0.8rem' } }}>
                {breadcrumbPath.map((step, i) => {
                  const stepNode = tree.nodes.find((n) => n.id === step.nodeId);
                  const isLast = i === breadcrumbPath.length - 1;
                  return (
                    <MuiLink
                      key={step.nodeId}
                      component="button"
                      underline={isLast ? 'none' : 'hover'}
                      color={isLast ? 'text.primary' : 'text.secondary'}
                      onClick={() => !isLast && setSelectedNodeId(step.nodeId)}
                      sx={{ fontWeight: isLast ? 700 : 400, cursor: isLast ? 'default' : 'pointer' }}
                    >
                      {step.viaLabel ? `"${step.viaLabel}" → ` : ''}{stepNode?.title || '(nodo eliminado)'}
                    </MuiLink>
                  );
                })}
              </Breadcrumbs>
            )}
            {breadcrumbPath.length === 0 && selectedNode.id !== tree.root_node_id && (
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                Este nodo todavía no está conectado desde el nodo inicial — nadie llega hasta acá siguiendo el algoritmo.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo de nodo</InputLabel>
                <Select
                  value={nodeForm.node_type}
                  label="Tipo de nodo"
                  onChange={(e) => handleNodeTypeChange(e.target.value)}
                >
                  {NODE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>

              <TextField
                label="Título / pregunta *"
                fullWidth
                value={nodeForm.title}
                onChange={(e) => setNodeForm((p) => ({ ...p, title: e.target.value }))}
                onBlur={() => handleNodeFieldBlur('title')}
                InputProps={{ endAdornment: savingIndicator('title') }}
              />

              <TextField
                label="Contenido (texto explicativo, conducta, diagnóstico)"
                fullWidth
                multiline
                rows={4}
                value={nodeForm.body_text}
                onChange={(e) => setNodeForm((p) => ({ ...p, body_text: e.target.value }))}
                onBlur={() => handleNodeFieldBlur('body_text')}
                helperText="Se guarda solo al salir del campo — no hace falta ningún botón."
              />

              <Divider />

              {/* ── Media ── */}
              <Typography variant="subtitle1">Imágenes y videos de referencia</Typography>
              {selectedNode.media.length > 0 && (
                <ImageList cols={3} rowHeight={140} gap={8}>
                  {selectedNode.media.map((m) => (
                    <ImageListItem key={m.id}>
                      <DecisionMedia media={m} sx={{ width: '100%', height: 140, objectFit: 'cover' }} />
                      <ImageListItemBar
                        title={m.caption || m.original_filename}
                        actionIcon={
                          <IconButton sx={{ color: 'white' }} size="small" onClick={() => handleDeleteMedia(m.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  disabled={uploading}
                >
                  Subir imagen o video
                  <input type="file" hidden accept="image/*,video/mp4,video/webm,video/quicktime" onChange={handleUploadMedia} />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Máx. {MAX_MEDIA_MB} MB · JPG, PNG, GIF, WEBP, MP4, WEBM, MOV
                </Typography>
              </Box>

              <Divider />

              {/* ── Opciones (ramas) existentes ── */}
              <Typography variant="subtitle1">Opciones (a dónde lleva cada respuesta)</Typography>
              {selectedNode.options.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedNode.options.map((o) => {
                    const target = tree.nodes.find((n) => n.id === o.to_node_id);
                    return (
                      <Paper key={o.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {o.image_media && (
                          <DecisionMedia media={o.image_media} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} />
                        )}
                        <Chip label={o.label} size="small" />
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        <Typography
                          variant="body2"
                          sx={{ flexGrow: 1, cursor: target ? 'pointer' : 'default', textDecoration: target ? 'underline' : 'none' }}
                          onClick={() => target && setSelectedNodeId(target.id)}
                        >
                          {target ? `#${nodeNumberById.get(target.id)} — ${target.title}` : '(nodo eliminado)'}
                        </Typography>
                        {o.rationale_text && (
                          <Tooltip title={o.rationale_text}>
                            <Chip label="¿por qué?" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                        <IconButton size="small" onClick={() => openEditOption(o)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteOption(o.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    );
                  })}
                </Box>
              )}

              {/* ── Agregar rama: crea nodo + opción juntos ── */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Agregar una rama desde acá</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Escribí la respuesta posible (ej: "Sí", "Líquido libre presente") — se crea un nodo nuevo con ese
                  mismo texto como título, ya conectado. Si el nodo destino necesita un título distinto al de la
                  opción, lo editás después seleccionándolo en la lista.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    label="Texto de la opción"
                    size="small"
                    value={branchLabel}
                    onChange={(e) => setBranchLabel(e.target.value)}
                    autoFocus={selectedNode.options.length === 0}
                  />
                  <TextField
                    label="Justificación clínica — '¿por qué?' (opcional)"
                    size="small"
                    multiline
                    rows={2}
                    value={branchRationale}
                    onChange={(e) => setBranchRationale(e.target.value)}
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" fullWidth disabled={selectedNode.media.length === 0}>
                      <InputLabel>Imagen de referencia (opcional)</InputLabel>
                      <Select
                        label="Imagen de referencia (opcional)"
                        value={branchImageId}
                        onChange={(e) => setBranchImageId(e.target.value)}
                      >
                        <MenuItem value=""><em>Sin imagen</em></MenuItem>
                        {selectedNode.media.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DecisionMedia media={m} sx={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 0.5 }} />
                              {m.caption || m.original_filename}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      startIcon={uploadingBranchImage ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" />}
                      disabled={uploadingBranchImage}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Subir nueva
                      <input
                        type="file"
                        hidden
                        accept="image/*,video/mp4,video/webm,video/quicktime"
                        onChange={handleUploadBranchImage}
                      />
                    </Button>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={creatingBranch && !linkExistingOpen ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    onClick={handleAddBranch}
                    disabled={creatingBranch || !branchLabel.trim()}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Crear nodo y conectar
                  </Button>

                  {otherNodes.length > 0 && (
                    <Box>
                      <Button
                        size="small"
                        onClick={() => setLinkExistingOpen((p) => !p)}
                        endIcon={linkExistingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        ¿Ya existe el nodo destino? Elegilo acá
                      </Button>
                      <Collapse in={linkExistingOpen}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Nodo existente</InputLabel>
                            <Select
                              label="Nodo existente"
                              value={linkExistingTarget}
                              onChange={(e) => setLinkExistingTarget(e.target.value)}
                            >
                              {otherNodes.map((n) => (
                                <MenuItem key={n.id} value={n.id}>#{nodeNumberById.get(n.id)} — {n.title}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            onClick={handleLinkExisting}
                            disabled={creatingBranch || !branchLabel.trim() || !linkExistingTarget}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            {creatingBranch && linkExistingOpen ? <CircularProgress size={16} /> : 'Conectar'}
                          </Button>
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ flexGrow: 1, p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Seleccioná o creá un nodo para empezar a editarlo</Typography>
          </Paper>
        )}
      </Box>

      <Dialog open={newNodeOpen} onClose={() => setNewNodeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo nodo suelto</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Este nodo no queda conectado a ningún otro — usalo para el primer nodo del algoritmo, o para armar una
            pieza suelta que vas a conectar después. Para el caso común (agregar el siguiente paso de una pregunta ya
            existente), usá "Agregar una rama desde acá" dentro del nodo seleccionado en vez de esto.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Tipo de nodo</InputLabel>
            <Select
              value={newNodeForm.node_type}
              label="Tipo de nodo"
              onChange={(e) => setNewNodeForm((p) => ({ ...p, node_type: e.target.value }))}
            >
              {NODE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Título / pregunta *"
            fullWidth
            autoFocus
            value={newNodeForm.title}
            onChange={(e) => setNewNodeForm((p) => ({ ...p, title: e.target.value }))}
          />
          <TextField
            label="Contenido (opcional)"
            fullWidth
            multiline
            rows={3}
            value={newNodeForm.body_text}
            onChange={(e) => setNewNodeForm((p) => ({ ...p, body_text: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewNodeOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateNode}>Crear</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editingOption} onClose={() => setEditingOption(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar opción</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Texto de la opción"
            fullWidth
            autoFocus
            value={editOptionForm.label}
            onChange={(e) => setEditOptionForm((p) => ({ ...p, label: e.target.value }))}
          />
          <FormControl fullWidth>
            <InputLabel>Lleva al nodo</InputLabel>
            <Select
              label="Lleva al nodo"
              value={editOptionForm.to_node_id}
              onChange={(e) => setEditOptionForm((p) => ({ ...p, to_node_id: e.target.value }))}
            >
              {tree?.nodes
                .filter((n) => n.id !== editingOption?.from_node_id)
                .map((n) => <MenuItem key={n.id} value={n.id}>#{nodeNumberById.get(n.id)} — {n.title}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Justificación clínica — '¿por qué?' (opcional)"
            fullWidth
            multiline
            rows={2}
            value={editOptionForm.rationale_text}
            onChange={(e) => setEditOptionForm((p) => ({ ...p, rationale_text: e.target.value }))}
          />
          {(() => {
            const fromNode = tree?.nodes.find((n) => n.id === editingOption?.from_node_id);
            if (!fromNode) return null;
            return (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl fullWidth disabled={fromNode.media.length === 0}>
                  <InputLabel>Imagen de referencia (opcional)</InputLabel>
                  <Select
                    label="Imagen de referencia (opcional)"
                    value={editOptionForm.image_media_id}
                    onChange={(e) => setEditOptionForm((p) => ({ ...p, image_media_id: e.target.value }))}
                  >
                    <MenuItem value=""><em>Sin imagen</em></MenuItem>
                    {fromNode.media.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DecisionMedia media={m} sx={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 0.5 }} />
                          {m.caption || m.original_filename}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={uploadingEditOptionImage ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" />}
                  disabled={uploadingEditOptionImage}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Subir nueva
                  <input
                    type="file"
                    hidden
                    accept="image/*,video/mp4,video/webm,video/quicktime"
                    onChange={handleUploadEditOptionImage}
                  />
                </Button>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOption(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveOption} disabled={savingOption}>
            {savingOption ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlgoritmoEditor;
